interface Separators {
  group: string;
  decimal: string;
}

/**
 * Reads the grouping and decimal separators the locale uses, via Intl.
 * Falls back to comma/dot if the locale tag is invalid.
 */
export const getSeparators = (locale: string): Separators => {
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(-11111.1);
    const group = parts.find((part) => part.type === 'group')?.value ?? ',';
    const decimal = parts.find((part) => part.type === 'decimal')?.value ?? '.';
    return { group, decimal };
  } catch {
    return { group: ',', decimal: '.' };
  }
};

/**
 * Normalizes a locale-formatted numeric string to the internal dot form that the
 * validation regex and parseFloat understand. Grouping-position-aware: a lone
 * separator followed by exactly three digits is treated as grouping only when it
 * is the locale's group separator, so a German keypad dot in "21.5" stays "21.5"
 * instead of being mis-stripped to "215". Non-numeric input is returned cleaned
 * and rejected downstream.
 */
export const parseLocaleNumber = (input: string, locale: string): string => {
  if (!input) {
    return input;
  }

  // Normalize the unicode minus and strip all whitespace (incl. NBSP / narrow NBSP
  // used as grouping separators in fr-FR / lv-LV).
  const cleaned = input
    .trim()
    .replace(/−/g, '-')
    .replace(/\s/g, '');

  const { group, decimal } = getSeparators(locale);
  const hasDot = cleaned.includes('.');
  const hasComma = cleaned.includes(',');

  // Both separators present → the last one is the decimal, the other is grouping.
  if (hasDot && hasComma) {
    const decimalChar = cleaned.lastIndexOf('.') > cleaned.lastIndexOf(',') ? '.' : ',';
    const groupChar = decimalChar === '.' ? ',' : '.';
    return cleaned.split(groupChar).join('').replace(decimalChar, '.');
  }

  const separator = hasDot ? '.' : (hasComma ? ',' : '');
  if (!separator) {
    return cleaned;
  }

  const occurrences = cleaned.split(separator).length - 1;
  if (occurrences > 1) {
    // Repeated single separator → grouping (e.g. 1.234.567).
    return cleaned.split(separator).join('');
  }

  // Single occurrence: grouping only if it sits in a valid 3-digit group position
  // AND the locale actually uses this character for grouping; otherwise it is the decimal.
  const trailingDigits = cleaned.length - cleaned.lastIndexOf(separator) - 1;
  if (trailingDigits === 3 && separator === group && separator !== decimal) {
    return cleaned.split(separator).join('');
  }
  return cleaned.replace(separator, '.');
};

/**
 * Formats a number using the locale's decimal separator, with fixed precision and
 * no thousands grouping (clean for an editable field).
 */
export const formatLocaleNumber = (
  value: number,
  locale: string,
  precision?: number,
): string =>
  value.toLocaleString(locale, {
    minimumFractionDigits: precision ?? 0,
    maximumFractionDigits: precision ?? 0,
    useGrouping: false,
  });
